import React, { Fragment, useState } from 'react';
import Select from '@components/Blackbird/Forms/Select/Select';
import { getTimezoneOffsetValue } from '../../../../utils/Timezones/Timezones';
import FrequencyForm from './FrequencyForm';
import {
  DAILY,
  DAILY_HOUR_DEFAULT,
  DAY_OF_WEEK_DEFAULT,
  frequencies,
  FREQUENCY_DEFAULT,
  HOURLY_DEFAULT_OPT,
  INGESTION_SCHEDULE_LABEL,
  WEEKLY,
} from './utils/constants';
import { ToggleButton } from '@3.0/Buttons';
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import cn from 'utils/cn';
import MenuBar from '@3.0/MenuBar';

export interface IngestionScheduleItem {
  id?: number | string;
  override: boolean;
  frequency: 'hourly' | 'daily' | 'weekly';
  time: string;
  timezone: string;
  schedule: number | string;
}

export interface IngestionScheduleFormData {
  ingestionSchedule: IngestionScheduleItem[];
}

interface IngestionOverrideFormProps {
  watch: UseFormWatch<FieldValues>;
  errors: FieldErrors<FieldValues>;
  register: UseFormRegister<FieldValues>;
  ingestionSchedule?: IngestionScheduleItem[];
  control: Control<FieldValues>;
  singleColumn?: boolean;
  setValue: UseFormSetValue<FieldValues>;
  isStandAlone?: boolean;
  optionalHeader?: boolean;
}

export default function IngestionOverrideForm({
  watch,
  errors,
  register,
  ingestionSchedule,
  control,
  singleColumn = true,
  setValue,
  isStandAlone = false,
  optionalHeader = false,
}: IngestionOverrideFormProps) {
  /**
   * ingestionSchedule:
   * [{
   *    override: true,
   *    frequency: 'daily', // One of ['hourly', 'daily', 'weekly'],
   *    time: '10:00',
   *    timezone: 'Etc/GMT+10',
   *    schedule: '', // either day of week or hourly interval
   * }]
   */
  const [frequency, setFrequency] = useState(
    ingestionSchedule && ingestionSchedule.length > 0
      ? ingestionSchedule[0].frequency
      : FREQUENCY_DEFAULT
  );

  const handleFrequencyChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    const frequency = evt.target.value as 'hourly' | 'daily' | 'weekly';
    setFrequency(frequency);

    switch (frequency) {
      case WEEKLY:
        setValue('ingestionSchedule.0.schedule', DAY_OF_WEEK_DEFAULT);
        setValue('ingestionSchedule.0.time', DAILY_HOUR_DEFAULT);
        setValue('ingestionSchedule.0.timezone', getTimezoneOffsetValue());
        break;

      case DAILY:
        setValue('ingestionSchedule.0.schedule', HOURLY_DEFAULT_OPT);
        setValue('ingestionSchedule.0.time', DAILY_HOUR_DEFAULT);
        setValue('ingestionSchedule.0.timezone', getTimezoneOffsetValue());
        break;

      case FREQUENCY_DEFAULT:
      default:
        setValue('ingestionSchedule.0.schedule', HOURLY_DEFAULT_OPT);
        break;
    }
  };

  const isOverrideEnabled = watch('ingestionSchedule.0.override');

  return (
    <section
      data-testid="ingestion-override-form"
      className={cn('tw:mb-10', isStandAlone && 'tw:mb-0')}
    >
      {!optionalHeader && <MenuBar label={INGESTION_SCHEDULE_LABEL} actionButtons={[]} />}

      <div className={cn('tw:p-6', singleColumn && 'tw:w-[448px]')}>
        <input type="text" className="tw:hidden" disabled {...register('ingestionSchedule.0.id')} />
        <div className="tw:mb-3">
          <Controller
            name="ingestionSchedule.0.override"
            control={control}
            render={({ field }) => (
              <ToggleButton
                onChange={field.onChange}
                onBlur={field.onBlur}
                checked={field.value ?? false}
                ref={field.ref}
                testId="toggle-automatic-ingestion"
                label={
                  isStandAlone
                    ? 'Schedule automatic ingestion'
                    : 'Set ingestion schedule for this data source'
                }
              />
            )}
          />
        </div>

        {isOverrideEnabled && (
          <Fragment>
            <Select
              label="Run ingestion"
              inputName="frequency"
              errors={errors}
              showErrorMessageBelow
              selectProps={{
                'data-testid': 'frequency-select',
                ...register('ingestionSchedule.0.frequency', {
                  required: 'Select the frequency',
                  onChange: handleFrequencyChange,
                  value: frequency,
                }),
              }}
            >
              {frequencies.map((item) => (
                <option value={item.value} key={item.value}>
                  {item.label}
                </option>
              ))}
            </Select>

            <FrequencyForm register={register} errors={errors} frequency={frequency} />
          </Fragment>
        )}
      </div>
    </section>
  );
}
